// src/components/ResetPassword.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import { validatePassword } from "../utils/validation";
import '../styles/PasswordFlows.css';

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<'authenticator' | 'sms' | null>(null);
  const [smsSent, setSmsSent] = useState(false);

  // Initialize CSRF token before the form POST
  useEffect(() => {
    axiosInstance.get('/health').catch(() => {});
  }, []);

  // Password strength logic (same as Signup)
  const passwordStrength = () => {
    if (password.length < 8) return { text: "Weak", color: "red" };
    if (
      !/[A-Z]/.test(password) ||
      !/[a-z]/.test(password) ||
      !/[0-9]/.test(password)
    ) {
      return { text: "Medium", color: "orange" };
    }
    return { text: "Strong", color: "green" };
  };

  const strength = passwordStrength();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    const pwCheck = validatePassword(password);
    if (!pwCheck.valid) {
      setError(pwCheck.message || "Password is invalid");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    // Optional: enforce minimum strength
    if (strength.text === "Weak") {
      setError(
        "Password is too weak. Must be at least 8 characters with uppercase, lowercase, and number.",
      );
      setLoading(false);
      return;
    }

    try {
      await axiosInstance.post(`/auth/reset-password/${token}`, {
        password,
        confirmPassword,
        mfaCode: mfaCode || undefined,
      });

      toast.success("Password reset successfully! Redirecting to login...");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      toast.error(
        err.response?.data?.error || "Invalid or expired reset link.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSendSMS = async () => {
    if (!phone.match(/^\+?[1-9]\d{1,14}$/)) {
      toast.error('Enter a valid phone number with country code (e.g., +1 555 123 4567)');
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post(`/auth/reset-password-send-sms/${token}`, { phone });
      setSmsSent(true);
      toast.success('SMS code sent to your phone!');
    } catch (err: any) {
      const error = err.response?.data?.error || 'Failed to send SMS code';
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hb-password-container hb-auth-split">
      {/* Welcoming side panel (visual only — form logic unchanged) */}
      <aside className="hb-auth-aside">
        <div className="hb-auth-orbs" aria-hidden="true">
          <span /><span /><span />
        </div>
        <div className="hb-auth-aside-inner">
          <div className="hb-auth-brand">
            <span className="hb-auth-brand-mark">S</span> Sumbitrbuddy
          </div>
          <h2 className="hb-auth-headline">
            One strong password from <span className="hb-auth-accent">picking back up</span>.
          </h2>
          <p className="hb-auth-lead">
            Set a fresh password and your whole job hunt — applications,
            interviews, offers — is right where you left it.
          </p>
          <ul className="hb-auth-points">
            <li>Encrypted end to end</li>
            <li>Only you can access your account</li>
            <li>Straight back to your board</li>
          </ul>
          <div className="hb-auth-chips">
            <span className="hb-auth-chip">Encrypted</span>
            <span className="hb-auth-chip hb-auth-chip-b">Secured</span>
          </div>
        </div>
      </aside>

      <div className="hb-auth-main">
      {/* Coach Message */}
      <div className="hb-coach-message">
        <span className="hb-coach-icon">🔑</span>
        <p>Create a strong new password to secure your account!</p>
      </div>

      <div className="hb-password-card">
        <h1 className="hb-password-title">Reset Your Password</h1>
        <p className="hb-password-subtitle">Make sure it's at least 8 characters with a mix of letters and numbers.</p>

        {message && (
          <div className="hb-alert hb-alert-success">
            <span>✅</span>
            <p>{message}</p>
          </div>
        )}
        {error && (
          <div className="hb-alert hb-alert-error">
            <span>⚠️</span>
            <p>{error}</p>
          </div>
        )}

        {!message && (
          <form onSubmit={handleSubmit} className="hb-password-form">
            <div className="hb-form-group">
              <label htmlFor="password" className="hb-label">New Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="hb-input"
                placeholder="Enter new password"
              />
              {password && (
                <div className={`hb-password-strength hb-strength-${strength.text.toLowerCase()}`}>
                  <span className="hb-strength-indicator"></span>
                  <span className="hb-strength-text">Strength: {strength.text}</span>
                </div>
              )}
              <p className="hb-helper-text">At least 8 characters: uppercase, lowercase, and numbers</p>
            </div>

            <div className="hb-form-group">
              <label htmlFor="confirmPassword" className="hb-label">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="hb-input"
                placeholder="Confirm your password"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="hb-helper-text hb-helper-error">Passwords don't match yet</p>
              )}
            </div>

            <div className="hb-form-group">
              <label htmlFor="mfaCode" className="hb-label">MFA Verification (if enabled)</label>
              
              {!mfaMethod ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <p style={{ color: '#666', marginBottom: '8px' }}>
                    If MFA is enabled on your account, choose your verification method:
                  </p>
                  <button
                    type="button"
                    onClick={() => setMfaMethod('authenticator')}
                    disabled={loading}
                    style={{
                      padding: '10px',
                      background: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.95em',
                    }}
                  >
                    📱 Authenticator App
                  </button>
                  <button
                    type="button"
                    onClick={() => setMfaMethod('sms')}
                    disabled={loading}
                    style={{
                      padding: '10px',
                      background: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.95em',
                    }}
                  >
                    💬 SMS Code
                  </button>
                </div>
              ) : mfaMethod === 'authenticator' ? (
                <div>
                  <input
                    id="mfaCode"
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    maxLength={6}
                    className="hb-input"
                    placeholder="Enter 6-digit code from authenticator"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setMfaMethod(null);
                      setMfaCode('');
                      setPhone('');
                      setSmsSent(false);
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: '#f0f0f0',
                      color: '#666',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                    }}
                  >
                    ← Change Method
                  </button>
                </div>
              ) : mfaMethod === 'sms' ? (
                <div>
                  {!smsSent ? (
                    <>
                      <input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="hb-input"
                        placeholder="+1 555 123 4567"
                      />
                      <button
                        type="button"
                        onClick={handleSendSMS}
                        disabled={loading || !phone}
                        style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          background: '#2196F3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          fontSize: '0.9em',
                          width: '100%',
                        }}
                      >
                        {loading ? 'Sending...' : 'Send Code'}
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        id="smsCode"
                        type="text"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                        maxLength={6}
                        className="hb-input"
                        placeholder="Enter 6-digit code from SMS"
                      />
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setMfaMethod(null);
                      setMfaCode('');
                      setPhone('');
                      setSmsSent(false);
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: '#f0f0f0',
                      color: '#666',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                    }}
                  >
                    ← Change Method
                  </button>
                </div>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={loading ? 'hb-btn hb-btn-primary hb-btn-loading' : 'hb-btn hb-btn-primary'}
            >
              {loading ? '⏳ Resetting...' : '🔐 Reset Password'}
            </button>
          </form>
        )}

        <div className="hb-password-footer">
          <a href="/login" className="hb-link">
            ← Back to Login
          </a>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ResetPassword;
